import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { loadGoogleMaps } from '../../helper/googleMapsLoader';
import { loadLeaflet } from '../../helper/leafletLoader';
import config from '../../config';

const ControlTowerMap = ({ 
    venues = [], 
    height = '520px', 
    title = 'Venue Map',
    autoAskLocation = false 
}) => {
    const mapRef = useRef(null);
    const googleMapInstanceRef = useRef(null);
    const leafletMapInstanceRef = useRef(null);
    const markersRef = useRef([]);
    const userMarkerRef = useRef(null);
    const placesServiceRef = useRef(null);
    const activeInfoWindowRef = useRef(null);
    const boundsRef = useRef(null);

    const [mapEngine, setMapEngine] = useState('google'); // 'google' | 'leaflet'
    const [isApiLoaded, setIsApiLoaded] = useState(false);
    const [isLocating, setIsLocating] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [locationNotice, setLocationNotice] = useState(null);
    const [billingNotice, setBillingNotice] = useState(false);

    // UAE Coordinates (Abu Dhabi / Dubai center)
    const DEFAULT_CENTER = { lat: 25.10, lng: 55.20 };
    const DEFAULT_ZOOM = 8;

    // Haversine formula to compute distance in KM
    const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
        const R = 6371;
        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLon = ((lon2 - lon1) * Math.PI) / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return Math.round(R * c * 10) / 10;
    };

    // Extract valid lat/lng from any venue format
    const extractPosition = useCallback((venue) => {
        if (!venue) return null;
        try {
            if (venue.location) {
                const loc = typeof venue.location === 'string'
                    ? JSON.parse(venue.location)
                    : venue.location;

                if (loc?.coordinates && Array.isArray(loc.coordinates)) {
                    const lng = parseFloat(loc.coordinates[0]);
                    const lat = parseFloat(loc.coordinates[1]);
                    if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
                        return { lat, lng };
                    }
                } else if (loc?.lat && loc?.lng) {
                    const lat = parseFloat(loc.lat);
                    const lng = parseFloat(loc.lng);
                    if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
                        return { lat, lng };
                    }
                }
            }

            const latVal = venue.latitude ?? venue.venue_latitude ?? venue.lat ?? venue.address?.latitude;
            const lngVal = venue.longitude ?? venue.venue_longitude ?? venue.lng ?? venue.address?.longitude;

            if (latVal !== undefined && lngVal !== undefined && latVal !== null && lngVal !== null && latVal !== '') {
                const lat = parseFloat(latVal);
                const lng = parseFloat(lngVal);
                if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
                    return { lat, lng };
                }
            }
        } catch (e) {
            console.warn('Error parsing coordinates for venue:', venue.id, e);
        }
        return null;
    }, []);

    // Switch to Leaflet engine safely
    const switchToLeaflet = useCallback(() => {
        console.warn('Switching to OpenStreetMap / Leaflet engine.');
        setMapEngine('leaflet');
        setBillingNotice(true);
        if (googleMapInstanceRef.current && mapRef.current) {
            mapRef.current.innerHTML = '';
            googleMapInstanceRef.current = null;
        }
    }, []);

    // Try Google Maps first, fallback to Leaflet on auth failure (e.g. BillingNotEnabledMapError)
    useEffect(() => {
        let cancelled = false;

        window.gm_authFailure = () => {
            if (!cancelled) {
                console.warn('Google Maps gm_authFailure caught (Billing or Key restriction). Switching to Leaflet.');
                switchToLeaflet();
            }
        };

        loadGoogleMaps()
            .then(() => {
                if (!cancelled) {
                    setIsApiLoaded(true);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.warn('Google Maps script load failed:', err.message);
                    switchToLeaflet();
                }
            });

        return () => { cancelled = true; };
    }, [switchToLeaflet]);

    // Cleanup maps on unmount
    useEffect(() => {
        return () => {
            if (leafletMapInstanceRef.current) {
                leafletMapInstanceRef.current.remove();
                leafletMapInstanceRef.current = null;
            }
        };
    }, []);

    // Ask location / Geolocation handler (Works with both engines)
    const handleAskLocation = useCallback(() => {
        if (!navigator.geolocation) {
            setLocationNotice({ type: 'danger', message: 'Geolocation is not supported by your browser.' });
            return;
        }

        setIsLocating(true);
        setLocationNotice(null);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                setUserLocation(pos);
                setIsLocating(false);

                // --- Google Maps Engine ---
                if (mapEngine === 'google' && googleMapInstanceRef.current && window.google?.maps) {
                    googleMapInstanceRef.current.panTo(pos);
                    googleMapInstanceRef.current.setZoom(13);

                    if (userMarkerRef.current) {
                        userMarkerRef.current.setMap(null);
                        userMarkerRef.current = null;
                    }

                    const userIcon = {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 9,
                        fillColor: '#0284c7',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 3,
                    };

                    const userMarker = new window.google.maps.Marker({
                        position: pos,
                        map: googleMapInstanceRef.current,
                        title: 'Your Current Location',
                        icon: userIcon,
                        zIndex: 9999
                    });

                    const userWindow = new window.google.maps.InfoWindow({
                        content: `<div style="padding:4px;font-family:sans-serif;font-size:12px;font-weight:700;color:#0284c7;">📍 You are here</div>`
                    });
                    userMarker.addListener('click', () => userWindow.open(googleMapInstanceRef.current, userMarker));
                    userMarkerRef.current = userMarker;
                }

                // --- Leaflet Engine ---
                if (mapEngine === 'leaflet' && leafletMapInstanceRef.current && window.L) {
                    const L = window.L;
                    leafletMapInstanceRef.current.setView([pos.lat, pos.lng], 13, { animate: true });

                    if (userMarkerRef.current) {
                        userMarkerRef.current.remove();
                        userMarkerRef.current = null;
                    }

                    const userIcon = L.divIcon({
                        html: `<div style="display:flex;align-items:center;justify-content:center;width:22px;height:22px;background:#0284c7;border-radius:50%;border:3px solid #fff;box-shadow:0 0 0 5px rgba(2,132,199,0.35);"></div>`,
                        iconSize: [22, 22],
                        iconAnchor: [11, 11],
                        className: ''
                    });

                    const userMarker = L.marker([pos.lat, pos.lng], { icon: userIcon, zIndexOffset: 9999 })
                        .addTo(leafletMapInstanceRef.current)
                        .bindPopup('<div style="font-family:sans-serif;font-size:12px;font-weight:700;color:#0284c7;">📍 You are here</div>');
                    userMarkerRef.current = userMarker;
                }

                // Find closest venue
                let closest = null;
                let minDistance = Infinity;

                venues.forEach(v => {
                    const vPos = extractPosition(v);
                    if (vPos) {
                        const dist = calculateDistanceKm(pos.lat, pos.lng, vPos.lat, vPos.lng);
                        if (dist < minDistance) {
                            minDistance = dist;
                            closest = { ...v, distance: dist };
                        }
                    }
                });

                if (closest) {
                    setLocationNotice({
                        type: 'success',
                        message: `Located! Nearest venue is "${closest.title || closest.business_name || 'Venue'}" (${closest.distance} km away).`
                    });
                } else {
                    setLocationNotice({
                        type: 'info',
                        message: `Located at ${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)}.`
                    });
                }
            },
            (err) => {
                setIsLocating(false);
                let msg = 'Unable to retrieve location.';
                if (err.code === 1) msg = 'Location access was denied. You can still browse venues on the map.';
                else if (err.code === 2) msg = 'Location position unavailable.';
                else if (err.code === 3) msg = 'Location request timed out.';
                setLocationNotice({ type: 'warning', message: msg });
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    }, [venues, extractPosition, mapEngine]);

    // Reset view to fit all markers
    const handleResetView = useCallback(() => {
        if (mapEngine === 'google' && googleMapInstanceRef.current && window.google?.maps) {
            if (boundsRef.current && !boundsRef.current.isEmpty()) {
                googleMapInstanceRef.current.fitBounds(boundsRef.current);
            } else {
                googleMapInstanceRef.current.setCenter(DEFAULT_CENTER);
                googleMapInstanceRef.current.setZoom(DEFAULT_ZOOM);
            }
        } else if (mapEngine === 'leaflet' && leafletMapInstanceRef.current) {
            if (boundsRef.current) {
                leafletMapInstanceRef.current.fitBounds(boundsRef.current, { padding: [30, 30] });
            } else {
                leafletMapInstanceRef.current.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM);
            }
        }
    }, [mapEngine, DEFAULT_CENTER, DEFAULT_ZOOM]);

    // Auto-ask location on load if prop passed
    useEffect(() => {
        if (autoAskLocation && !userLocation && !isLocating) {
            handleAskLocation();
        }
    }, [autoAskLocation, userLocation, isLocating, handleAskLocation]);

    // Render Google Maps Engine
    useEffect(() => {
        if (mapEngine !== 'google' || !isApiLoaded || !window.google?.maps) return;

        try {
            if (!googleMapInstanceRef.current && mapRef.current) {
                googleMapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                    center: DEFAULT_CENTER,
                    zoom: DEFAULT_ZOOM,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: true,
                    clickableIcons: true,
                });
                placesServiceRef.current = new window.google.maps.places.PlacesService(googleMapInstanceRef.current);
            }

            if (googleMapInstanceRef.current && Array.isArray(venues)) {
                markersRef.current.forEach(m => m.setMap && m.setMap(null));
                markersRef.current = [];

                const bounds = new window.google.maps.LatLngBounds();
                let validCount = 0;

                venues.forEach(venue => {
                    const position = extractPosition(venue);
                    if (!position) return;
                    validCount++;
                    bounds.extend(position);

                    const title = venue.title || venue.business_name || venue.name || 'Venue';
                    const city = venue.city || venue.address_city || venue.address?.city || venue.country || '';
                    const category = venue.category_name || venue.business_category || 'Hospitality';

                    const rawImage = venue.thumbnail || venue.logo || venue.thumbnail_url || (venue.gallery && venue.gallery[0]?.image_url);
                    const imageUrl = rawImage ? (rawImage.startsWith('http') ? rawImage : `${config.API_BASE_URL}/${rawImage.replace(/^\//, '')}`) : null;
                    const imageHtml = imageUrl ? `<img src="${imageUrl}" alt="${title}" style="width:100%;height:105px;object-fit:cover;border-radius:6px;margin-bottom:8px;" onError="this.style.display='none'"/>` : '';
                    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${position.lat},${position.lng}`;

                    const infoWindow = new window.google.maps.InfoWindow({
                        content: `
                            <div style="padding:4px;min-width:210px;max-width:250px;font-family:sans-serif;">
                                ${imageHtml}
                                <div style="font-weight:700;font-size:14px;color:#0f172a;margin-bottom:2px;">${title}</div>
                                <div style="display:flex;gap:4px;align-items:center;margin-bottom:6px;">
                                    <span style="font-size:10px;font-weight:600;padding:2px 6px;border-radius:4px;background:#e0f2fe;color:#0369a1;">${category}</span>
                                    ${city ? `<span style="font-size:11px;color:#64748b;">• ${city}</span>` : ''}
                                </div>
                                <div style="display:flex;justify-content:space-between;align-items:center;padding-top:6px;border-top:1px solid #f1f5f9;">
                                    <span style="font-size:11px;font-weight:600;color:${venue.verification_status === 'approved' ? '#16a34a' : '#d97706'};">
                                        ${venue.verification_status === 'approved' ? '✓ Verified' : 'Pending'}
                                    </span>
                                    <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" style="font-size:11px;color:#0284c7;text-decoration:none;font-weight:600;">Directions ↗</a>
                                </div>
                            </div>
                        `
                    });

                    const marker = new window.google.maps.Marker({
                        position,
                        map: googleMapInstanceRef.current,
                        title
                    });

                    marker.addListener('click', () => {
                        if (activeInfoWindowRef.current) activeInfoWindowRef.current.close();
                        infoWindow.open(googleMapInstanceRef.current, marker);
                        activeInfoWindowRef.current = infoWindow;
                    });

                    markersRef.current.push(marker);
                });

                boundsRef.current = bounds;
                if (validCount > 0 && !userLocation) {
                    googleMapInstanceRef.current.fitBounds(bounds);
                }
            }
        } catch (err) {
            console.warn('Google Maps render failed, switching to Leaflet:', err);
            switchToLeaflet();
        }
    }, [mapEngine, isApiLoaded, venues, extractPosition, userLocation, switchToLeaflet]);

    // Render Leaflet Engine (Fallback / Active when Google Billing is disabled)
    useEffect(() => {
        if (mapEngine !== 'leaflet') return;

        loadLeaflet().then(L => {
            if (!mapRef.current) return;

            if (!leafletMapInstanceRef.current) {
                const map = L.map(mapRef.current, {
                    center: [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng],
                    zoom: DEFAULT_ZOOM,
                    zoomControl: false,
                    attributionControl: true
                });

                L.control.zoom({ position: 'bottomright' }).addTo(map);

                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '&copy; OpenStreetMap contributors',
                    maxZoom: 19
                }).addTo(map);

                leafletMapInstanceRef.current = map;
            }

            const map = leafletMapInstanceRef.current;
            markersRef.current.forEach(m => m.remove && m.remove());
            markersRef.current = [];

            const bounds = L.latLngBounds();
            let validCount = 0;

            venues.forEach(venue => {
                const position = extractPosition(venue);
                if (!position) return;
                validCount++;
                bounds.extend([position.lat, position.lng]);

                const title = venue.title || venue.business_name || venue.name || 'Venue';
                const city = venue.city || venue.address_city || venue.address?.city || venue.country || '';
                const category = venue.category_name || venue.business_category || 'Hospitality';

                const rawImage = venue.thumbnail || venue.logo || venue.thumbnail_url || (venue.gallery && venue.gallery[0]?.image_url);
                const imageUrl = rawImage ? (rawImage.startsWith('http') ? rawImage : `${config.API_BASE_URL}/${rawImage.replace(/^\//, '')}`) : null;
                const imageHtml = imageUrl ? `<img src="${imageUrl}" alt="${title}" style="width:100%;height:100px;object-fit:cover;border-radius:6px;margin-bottom:8px;" onError="this.style.display='none'"/>` : '';
                const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${position.lat},${position.lng}`;

                const pinIcon = L.divIcon({
                    html: `<div style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;background:#0284c7;color:#fff;border-radius:50%;border:2px solid #fff;box-shadow:0 3px 6px rgba(0,0,0,0.3);font-size:16px;">📍</div>`,
                    iconSize: [32, 32],
                    iconAnchor: [16, 32],
                    popupAnchor: [0, -32],
                    className: ''
                });

                const marker = L.marker([position.lat, position.lng], { icon: pinIcon }).addTo(map);
                marker.bindPopup(`
                    <div style="font-family:sans-serif;min-width:210px;max-width:250px;color:#0f172a;">
                        ${imageHtml}
                        <div style="font-weight:700;font-size:14px;margin-bottom:2px;">${title}</div>
                        <div style="display:flex;gap:4px;align-items:center;margin-bottom:6px;">
                            <span style="font-size:10px;font-weight:600;padding:2px 6px;border-radius:4px;background:#e0f2fe;color:#0369a1;">${category}</span>
                            ${city ? `<span style="font-size:11px;color:#64748b;">• ${city}</span>` : ''}
                        </div>
                        <div style="display:flex;justify-content:space-between;align-items:center;padding-top:6px;border-top:1px solid #f1f5f9;">
                            <span style="font-size:11px;font-weight:600;color:${venue.verification_status === 'approved' ? '#16a34a' : '#d97706'};">
                                ${venue.verification_status === 'approved' ? '✓ Verified' : 'Pending'}
                            </span>
                            <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" style="font-size:11px;color:#0284c7;text-decoration:none;font-weight:600;">Directions ↗</a>
                        </div>
                    </div>
                `, { maxWidth: 280 });

                markersRef.current.push(marker);
            });

            boundsRef.current = bounds;
            if (validCount > 0 && !userLocation) {
                map.fitBounds(bounds, { padding: [30, 30] });
            }
        }).catch(err => {
            console.error('Leaflet load error:', err);
        });

        return () => {
            if (leafletMapInstanceRef.current) {
                try {
                    leafletMapInstanceRef.current.remove();
                } catch (e) {}
                leafletMapInstanceRef.current = null;
            }
        };
    }, [mapEngine, venues, extractPosition, userLocation]);

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden shadow-sm border">
            {/* Map Header with Controls */}
            <div className="card-header border-bottom bg-white py-14 px-20 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                    <div className="w-32-px h-32-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                        <Icon icon="solar:map-point-wave-bold" className="text-lg" />
                    </div>
                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <h6 className="text-md fw-bold mb-0 text-dark">{title}</h6>
                            {mapEngine === 'leaflet' && (
                                <span className="badge bg-light text-secondary border px-8 py-2 rounded-pill text-2xs fw-semibold">
                                    OpenStreetMap
                                </span>
                            )}
                            {mapEngine === 'google' && (
                                <span className="badge bg-primary-50 text-primary-600 border border-primary-100 px-8 py-2 rounded-pill text-2xs fw-semibold">
                                    Google Maps
                                </span>
                            )}
                        </div>
                        <span className="text-xs text-secondary-light">
                            {venues.length} {venues.length === 1 ? 'venue' : 'venues'} loaded
                            {userLocation ? ' • Centered near you' : ''}
                        </span>
                    </div>
                </div>

                {/* Map Control Buttons: Ask Location & Fit All */}
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className={`btn btn-sm ${userLocation ? 'btn-success' : 'btn-primary'} radius-8 d-inline-flex align-items-center gap-1 shadow-2xs`}
                        onClick={handleAskLocation}
                        disabled={isLocating}
                        title="Ask browser permission to locate me and show nearby venues"
                    >
                        {isLocating ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" style={{ width: 14, height: 14 }} />
                                <span>Locating...</span>
                            </>
                        ) : (
                            <>
                                <Icon icon="solar:gps-bold" className="text-base" />
                                <span>{userLocation ? 'Re-center on Me' : 'My Location'}</span>
                            </>
                        )}
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary radius-8 d-inline-flex align-items-center gap-1"
                        onClick={handleResetView}
                        title="Show all venues on map"
                    >
                        <Icon icon="solar:restart-bold" />
                        <span>Show All Venues</span>
                    </button>
                </div>
            </div>

            {/* Google Cloud Billing Alert Banner */}
            {billingNotice && (
                <div className="py-8 px-16 text-xs d-flex align-items-center justify-content-between bg-warning-50 text-warning-main border-bottom">
                    <div className="d-flex align-items-center gap-2">
                        <Icon icon="solar:info-circle-bold" className="text-base flex-shrink-0" />
                        <span>
                            <strong>Google Cloud Billing required:</strong> Google Maps key reported <code>BillingNotEnabledMapError</code>. Map is operating live on OpenStreetMap fallback. 
                            <a 
                                href="https://console.cloud.google.com/project/_/billing/enable" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-decoration-underline ms-1 fw-bold text-dark"
                            >
                                Link Billing Account to Google Cloud Project ↗
                            </a>
                        </span>
                    </div>
                    <button
                        type="button"
                        className="btn btn-link p-0 text-secondary text-decoration-none ms-2"
                        onClick={() => setBillingNotice(false)}
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Location Notice Banner if any */}
            {locationNotice && (
                <div className={`py-8 px-16 text-xs d-flex align-items-center justify-content-between bg-${locationNotice.type === 'success' ? 'success' : locationNotice.type === 'danger' ? 'danger' : 'info'}-50 text-${locationNotice.type === 'success' ? 'success-main' : locationNotice.type === 'danger' ? 'danger-main' : 'info-main'} border-bottom`}>
                    <div className="d-flex align-items-center gap-2">
                        <Icon icon={locationNotice.type === 'success' ? 'solar:check-circle-bold' : 'solar:info-circle-bold'} className="text-sm" />
                        <span className="fw-medium">{locationNotice.message}</span>
                    </div>
                    <button
                        type="button"
                        className="btn btn-link p-0 text-secondary text-decoration-none"
                        onClick={() => setLocationNotice(null)}
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Map Canvas */}
            <div className="card-body p-0 position-relative" style={{ height }}>
                <div ref={mapRef} style={{ height: '100%', width: '100%' }} />

                {/* Floating On-Map GPS Pill */}
                <button
                    type="button"
                    className="btn btn-light shadow-sm position-absolute rounded-circle p-0 d-flex align-items-center justify-content-center"
                    style={{
                        bottom: 24,
                        right: 24,
                        width: 44,
                        height: 44,
                        zIndex: 1000,
                        border: '1px solid rgba(0,0,0,0.1)'
                    }}
                    onClick={handleAskLocation}
                    disabled={isLocating}
                    title="Use My Location"
                >
                    {isLocating ? (
                        <span className="spinner-border spinner-border-sm text-primary" style={{ width: 18, height: 18 }} />
                    ) : (
                        <Icon icon="solar:gps-bold" className="text-xl text-primary-600" />
                    )}
                </button>
            </div>
        </div>
    );
};

export default ControlTowerMap;
